<script setup>
import Spinner from '@/components/Spinner.vue';
import { debounce } from '@/helpers';
import { useAuthStore } from '@/stores/auth';
import { useGamesStore } from '@/stores/games';
import { storeToRefs } from 'pinia';
import { ref, watch, onMounted} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import FunPlayModal from './Partials/FunPlayModal.vue';
import Carousel from '@/components/App/Carousel.vue';
import { RouterLink } from 'vue-router'
const assetsUrl = import.meta.env.VITE_ASSETS_URL

const { t } = useI18n()
const router = useRouter()
const auth = useAuthStore()
const games = useGamesStore()
const { 
    getCategories, 
    getSubcategories, 
    getGames,
    getNewLCGames,
    getProvidersByCategory,
    getLCRecents
} = storeToRefs(games)
const { getCredits, getCurrency, getUser, isAuth } = storeToRefs(auth)

const showSpinner = ref(false)
const opening = ref(false)
const search = ref('')
const searchResult = ref([])
const selectedGame = ref()
const modalRef = ref(null);
const nav_lang = navigator.language || navigator.userLanguage
const lang = ref(localStorage.getItem('lang') || nav_lang)

const handlePlay = ({ demo }) => {
  launchGame(selectedGame.value, demo)
}
const openGame = (game) => {
    if(isAuth.value){
        if (game.has_demo === 'Y' && getUser.value?.c_p_demo) {
            selectedGame.value = game
            modalRef.value.openModal()
        } else {
            launchGame(game, false)
        }
    }else{
         router.push({ name: 'login' })
    }
}
const launchGame = async (game, isDemo) => {
  opening.value = true;
  try {
    const res = await games.openGame(game._id, {
      device: 'M',
      currency_id: getCredits.value[0].cuid._id,
      currency_name: getCurrency.value(getCredits.value[0].cuid._id).cod,
      homeURL: import.meta.env.VITE_APP_URL,
      demo: isDemo,
      lang: lang.value
    });

    opening.value = false;

    if (res.data?.nt === true) {
        window.open(res.data.data, '_blank');
        return;
    }

    if (res.data?.nt === false) {
        games.setGameUrl(res.data.data);
        router.push('/casino/game');
    }

  } catch (error) {
    console.log(error)
    opening.value = false;
  }
};

const startSearching = debounce(async () => {
    showSpinner.value = true
    await games.searchGames(search.value).then(res => {
        if (res.data.ok) {
            searchResult.value = res.data.data
            showSpinner.value = false
        }
    })
}, 500)

watch(search, (value) => {
    startSearching()
})
onMounted(async () => {
    if(isAuth.value){
        games.fetchRecentsGames() 
    }
})
const sliderImages = [
    { src: '/banner/lc1.png', url: '/live-casino' },
    { src: '/banner/lc2.png', url: '/live-casino' },
    { src: '/banner/lc3.png', url: '/live-casino' },
    { src: '/banner/lc4.png', url: '/live-casino' },
]
</script>

<template>
    <div>
        <div class="SearchInput">
            <div class="SearchInput_Icon" />
            <input
                class="SearchInput_Field"
                :placeholder="$t('app.8')"
                type="search"
                v-model="search"
            />
        </div>
        <div v-show="!search">
            <div class="py-5 px-3">
                <Carousel
                    height="250px"
                    :items="sliderImages"
                    :slidesPerView="1"
                    :spaceBetween="20"
                    :showNavigation="false"
                    :showPagination="true"
                    :autoplay="{ delay: 2000, disableOnInteraction: false }"
                    :swiperOptions="{ loop: true }"
                >
                    <template #slide="{ slide }">
                        <RouterLink :to="slide.url" class="block w-full h-full">
                            <img :src="slide.src" alt="" class="carousel-img rounded-lg cursor-pointer"/>
                        </RouterLink>
                    </template>
                </Carousel>
            </div>
            <div class="px-4">
                <ul class="list-none space-y-6">
                   <!-- all providers per Live Casino -->
                    <div v-if="getCategories.find(c => c.nm === 'Live Casino')">
                        <h3 class="text-lg font-bold text-white/75 cursor-pointer">
                            {{ $t('games.providers') }}
                        </h3>
                        <ul class="flex overflow-x-auto space-x-2 mt-3">
                            <li 
                            v-for="provider in getProvidersByCategory(getCategories.find(c => c.nm === 'Live Casino')._id).sort((a, b) => a.ord - b.ord)" 
                            :key="provider._id"
                            class="flex-1 px-2 bg-neutral-600/25 rounded-lg flex items-center justify-center"
                            >
                            <RouterLink :to="`/casino/${getCategories.find(c => c.nm === 'Live Casino')._id}/providers`" class="block w-[120px]">
                                <img 
                                :src="provider.img" 
                                class="game-thumb h-[56px] w-full" 
                                :alt="provider.nm" 
                                loading="lazy" 
                                />
                            </RouterLink>
                            </li>
                        </ul>
                    </div>
                    <!-- Recents Games -->
                    <li v-if="getLCRecents.length">
                        <h3 class="text-lg font-bold text-white/75 cursor-pointer">{{ $t('app.39') }}</h3>
                        <ul class="flex overflow-x-auto space-x-4 mt-3">
                            <li v-for="game in getLCRecents" class="flex-1">
                                <div class="w-[200px]" @click="openGame(game)">
                                    <img 
                                        v-img-fallback="[`${assetsUrl}/c/g/old/${game.img}`, `${assetsUrl}/c/g/${game.img}`, game.extimg]"   
                                        class="game-thumb rounded-xl" 
                                        alt="" loading="lazy"
                                    >
                                </div>
                            </li>
                        </ul>
                    </li>
                    <!-- New Games -->
                    <div v-if="getNewLCGames.length">
                        <h3 class="flex items-center gap-2 text-lg font-bold text-white/75 cursor-pointer">
                            <img src="/fire.svg"  class="w-4 h-4 "/>
                            {{ $t('app.40') }}
                        </h3>
                        <ul class="flex overflow-x-auto space-x-4 mt-3">
                            <li v-for="game in getNewLCGames" class="flex-1">
                                <div class="w-[200px]" @click="openGame(game)">
                                    <img 
                                        v-img-fallback="[`${assetsUrl}/c/g/old/${game.img}`, `${assetsUrl}/c/g/${game.img}`, game.extimg]"   
                                        class="game-thumb rounded-xl" 
                                        alt="" loading="lazy"
                                    >
                                </div>
                            </li>
                        </ul>
                    </div>

                    <!-- top games -->
                    <div>
                        <h3 class="text-lg font-bold text-white/75 cursor-pointer mt-3" v-if="getGames('675839524f20898d9b11ddea', '6972962b85fd075925d71a50').length">
                            {{ $t('games.popular') }}
                        </h3>
                        <ul class="flex overflow-x-auto space-x-4 mt-3">
                            <li v-for="game in getGames('675839524f20898d9b11ddea', '6972962b85fd075925d71a50')" class="flex-1">
                                <div class="w-[200px]" @click="openGame(game)">
                                    <img 
                                        v-img-fallback="[`${assetsUrl}/c/g/old/${game.img}`, `${assetsUrl}/c/g/${game.img}`, game.extimg]"   
                                        class="game-thumb rounded-xl" 
                                        alt="" loading="lazy"
                                    >
                                </div>
                            </li>
                        </ul>
                    </div>

                    <!-- <li v-for="cat in getCategories.filter(c => c.nm === 'Live Casino')">
                        <div>
                            <h3 class="text-lg font-bold text-white/75 cursor-pointer mt-3">
                            {{ $t('games.' + cat.nm.replace(' ', '_').toLowerCase()) }}
                            </h3>

                            <ul v-for="sub in getSubcategories(cat._id).filter(s => s.main)" class="flex overflow-x-auto space-x-4 mt-3">
                            <li v-for="game in getGames(cat._id, sub._id)" class="flex-1">
                                <div class="w-[200px]" @click="openGame(game)">
                                <img :src="game.img ? `${assetsUrl}/c/g/${game.img}` : game.extimg"  class="game-thumb rounded-xl" alt="" loading="lazy">
                                </div>
                            </li>
                            </ul>
                        </div>
                    </li> -->
                </ul>
            </div>
        </div>

        <div v-show="search" class="px-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-6 space-x-1">
            <template v-for="game in searchResult">
                <div class="cursor-pointer" @click="openGame(game)">
                    <img 
                        v-img-fallback="[`${assetsUrl}/c/g/old/${game.img}`, `${assetsUrl}/c/g/${game.img}`, game.extimg]"   
                        class="game-thumb rounded-xl" 
                        alt="" loading="lazy"
                    >
                </div>
            </template>
        </div>

        <FunPlayModal
            ref="modalRef"
            :value="selectedGame"
            @play="handlePlay"
        />
        <Spinner :show="opening || showSpinner" />
    </div>
</template>

<style scoped>
.ActiveFilter {
    /* filter: invert(37%) sepia(60.5%) saturate(460%) hue-rotate(108.5deg) brightness(125%) contrast(115%); */
    color:var(--color-primary4);
    .cat-icon{
        background-color:var(--color-primary4);
    }
}
.cat-icon{
    mask-position: center;
    /* mask-size: contain; */
    mask-repeat: no-repeat;
    background-color: #BBBBBB; 
}
.icon-home {
  mask-image: url("/home.svg");

}
.icon-lc {
  mask-image: url("/livecasino.svg");

}
.icon-slots{
  mask-image: url("/slots.svg");
}
.icon-virtual{
  mask-image: url("/virtual.svg");
}
.icon-new{
  mask-image: url("/new.svg");
  background-color: #cd6505 ; 
}
.SearchInput {
    background-color: hsla(0,0%,100%,.1);
    border-radius: 50px;
    display: flex;
    @apply mx-3 mt-2 px-3;
}

.SearchInput_Icon {
    mask-image: url("/search.svg");
    mask-size: 18px 18px;
    mask-repeat: no-repeat;
    mask-position: center;
    min-width: 18px;
    background-color: #fff;
    z-index: 20;
    display: flex;
    flex-direction: column
}

.SearchInput_Field {
    @apply flex-1 text-sm !bg-transparent placeholder:text-white font-bold text-white py-2.5 border-0;
    @apply focus:ring-0;
}
.game-thumb {
 height: auto;
  width: 100%;
  min-height: 2px;    
  object-fit: cover;
}
.carousel-img {
  width: 100%;
  height: 250px; 
  object-fit: cover;
  border-radius: 0.5rem;
}
</style>
